// Phase R2 — Prompt composer (design refactor).
//
// Visual port of the Claude Design handoff `PromptComposer`
// (extractor-ui.jsx) onto the se-* token namespace: card chrome,
// mono eyebrow, Playfair title, square quick-pick chips, framed
// input.
//
// Contract-frozen: props stay `{ value, onChange, disabled }` — this
// component owns NO submit logic (the design's "Run Pass 1" button
// belongs to SlipExtractorForm, R3). Every testid, the
// QUICK_PICK_CHIPS export, the 6 chips, the 5/2000 bounds, applyChip
// stacking, and the N11 invariant are preserved verbatim.

import { SE } from "./seTokens.ts";

export interface PromptComposerProps {
  /** Controlled value of the prompt textarea. */
  value: string;
  /** Parent receives every keystroke + every chip insertion. */
  onChange: (value: string) => void;
  /** Disabled while the parent submits or the slip is missing. */
  disabled?: boolean;
}

/**
 * The six locked-in chips (spec §6.3 v1.2). Each chip's `text` is
 * exactly what gets inserted; the intent parser interprets natural
 * language. N11 invariant: every chip is math- or user-positive —
 * none invoke loss-history penalties.
 */
export const QUICK_PICK_CHIPS: ReadonlyArray<{
  id: string;
  label: string;
  text: string;
}> = [
  {
    id: "trim-risky",
    label: "Trim risky legs",
    text: "Trim legs the scorer rates below 50% win probability.",
  },
  {
    id: "fifty-odds",
    label: "Build a 50-odds version",
    text: "Build a subset targeting combined odds of about 50.",
  },
  {
    id: "two-hundred-odds",
    label: "Build a 200-odds version",
    text: "Build a subset targeting combined odds of about 200.",
  },
  {
    id: "best-4-by-ev",
    label: "Best 4-leg subset by EV",
    text: "Give me the highest expected-value 4-leg subset from this slip.",
  },
  {
    id: "strong-markets",
    label: "Keep only my strong markets",
    text:
      "Keep only legs in markets where my historical win rate beats the population mean.",
  },
  {
    id: "highest-pwin-3",
    label: "Highest hit-probability 3-leg combo",
    text: "Give me the 3 legs with the highest predicted win probability.",
  },
];

const MIN_PROMPT_LEN = 5;
const MAX_PROMPT_LEN = 2000;

export function PromptComposer({
  value,
  onChange,
  disabled,
}: PromptComposerProps): JSX.Element {
  const charsLeft = MAX_PROMPT_LEN - value.length;
  const isReady = value.trim().length >= MIN_PROMPT_LEN;

  function applyChip(text: string): void {
    const next = value.length === 0 ? text : `${value.trim()}\n${text}`;
    onChange(next.slice(0, MAX_PROMPT_LEN));
  }

  return (
    <div
      data-testid="sle-prompt-composer"
      style={{
        background: SE.surfaceCard,
        border: `1px solid ${SE.border}`,
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header — mono eyebrow + Playfair title */}
      <div style={{ padding: "22px 26px 14px" }}>
        <div
          style={{
            fontFamily: SE.fontMono,
            fontSize: 10,
            letterSpacing: "0.22em",
            color: SE.textMuted,
            textTransform: "uppercase",
          }}
        >
          {disabled ? "Pass review" : "Pass instruction"}
        </div>
        <div
          style={{
            fontFamily: SE.fontDisplay,
            fontSize: 19,
            fontWeight: 500,
            color: SE.text,
            marginTop: 8,
          }}
        >
          Tell STRIKZA what to do with this slip.
        </div>
      </div>

      <div style={{ padding: "0 26px 22px" }}>
        {/* Quick-pick chip row */}
        <div
          data-testid="sle-chip-row"
          role="toolbar"
          aria-label="Quick-pick prompts"
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          {QUICK_PICK_CHIPS.map((chip) => (
            <button
              type="button"
              key={chip.id}
              onClick={() => applyChip(chip.text)}
              disabled={disabled}
              data-testid={`sle-chip-${chip.id}`}
              style={{
                padding: "5px 11px",
                borderRadius: 2,
                border: `1px solid ${SE.border}`,
                background: "transparent",
                fontFamily: SE.fontBody,
                fontSize: 12,
                color: SE.textSec,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.5 : 1,
                transition: "all 120ms ease",
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Framed input (design's value box, kept editable) */}
        <label
          htmlFor="sle-prompt-textarea"
          style={{
            display: "block",
            fontFamily: SE.fontMono,
            fontSize: 9.5,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: SE.textMuted,
            marginBottom: 8,
          }}
        >
          Your request
        </label>
        <textarea
          id="sle-prompt-textarea"
          data-testid="sle-prompt-textarea"
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, MAX_PROMPT_LEN))}
          disabled={disabled}
          rows={3}
          placeholder="e.g. give me the best 200-odds 4-leg subset, drop anything below 50% win probability"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "16px 18px",
            background: SE.surfacePage,
            border: `1px solid ${SE.border}`,
            borderRadius: 2,
            color: SE.text,
            fontFamily: SE.fontBody,
            fontSize: 15,
            lineHeight: 1.5,
            resize: "vertical",
            minHeight: 64,
            outline: "none",
          }}
        />

        {/* Char counter + readiness hint */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 8,
            fontFamily: SE.fontMono,
            fontSize: 10.5,
            letterSpacing: "0.10em",
            color: SE.textMuted,
          }}
        >
          <span data-testid="sle-prompt-readiness">
            {isReady ? "Ready" : `Add at least ${MIN_PROMPT_LEN} characters`}
          </span>
          <span data-testid="sle-prompt-charsleft">
            {charsLeft} chars left
          </span>
        </div>
      </div>
    </div>
  );
}
